import { useMemo } from "react";

import {
    TokenListMetadata,
    useLoopscaleWalletContext,
    useOraclePrices,
    useSwapRoutes,
    useTokenListContext,
    useUserTokens
} from "@bridgesplit/abf-react";
import { maxSwapRouteMinInputUsd, SwapRouteMinInputUsd } from "app/utils/domain/swap-routes";

import { SwapFormState } from "./types";

export interface SwapPayWithView {
    /** Viable pay-with inputs (atomic configured routes with token-list metadata); undefined → no picker. */
    payWithTokens: TokenListMetadata[] | undefined;
    /** Sell token after route-driven auto-selection; falls back to the form's token. */
    effectiveSellToken: TokenListMetadata | undefined;
    /** Form with the auto-selected sell token applied; identity-stable when no auto-selection. */
    effectiveForm: SwapFormState;
    /** Intermediate token symbol when the selected route is multi-hop. */
    routeIntermediateSymbol: string | undefined;
    /** Selected route may settle async depending on the quote (e.g. RWA solver fill vs DEX). */
    routeAsyncPossible: boolean;
    /** Every configured exit from the buy token is always-async — a flipped swap could never settle atomically. */
    flipDisabled: boolean;
    /** Per-step minimum on the selected route (e.g. Glow vault deposit min), valued in USD. */
    routeMinInput: SwapRouteMinInputUsd | undefined;
}

/**
 * Feature view hook for data-driven pay-with routing in the swap flow. Composes configured pandora
 * routes + wallet balances + the form into pure derived state (no setState on data arrival), so
 * tokens without configured routes behave exactly as before — every field resolves undefined/false.
 */
export function useSwapPayWithView({
    form,
    sellTokenOverridden
}: {
    form: SwapFormState;
    /** Once the user explicitly picks/flips the sell token, auto-selection stops. */
    sellTokenOverridden: boolean;
}): SwapPayWithView {
    const {
        data: { userWalletAddress: userWallet }
    } = useLoopscaleWalletContext();
    const { getMetadata } = useTokenListContext();

    // Configured pay-with routes into the buy token (empty for most tokens — behavior unchanged).
    const { atomicRoutes: payWithRoutes } = useSwapRoutes({ outputMint: form.buyToken?.mint });
    // Configured routes out of the buy token, to know if a flipped swap could ever settle atomically.
    const { routes: buyTokenExitRoutes } = useSwapRoutes({ inputMint: form.buyToken?.mint });
    const { tokens: userTokens } = useUserTokens();

    const balanceByMint = useMemo(() => {
        const balances = new Map<string, number>();
        userTokens?.forEach((token) => {
            balances.set(token.mint, (balances.get(token.mint) ?? 0) + (token.tokenAmount.uiAmount ?? 0));
        });
        return balances;
    }, [userTokens]);

    // Viable pay-with inputs: atomic configured routes whose input has token-list metadata.
    const payWithTokens = useMemo(() => {
        if (!payWithRoutes?.length) return undefined;
        const seen = new Set<string>();
        const tokens: TokenListMetadata[] = [];
        for (const route of payWithRoutes) {
            if (seen.has(route.inputMint)) continue;
            seen.add(route.inputMint);
            const metadata = getMetadata(route.inputMint);
            if (metadata) tokens.push(metadata);
        }
        return tokens.length ? tokens : undefined;
    }, [payWithRoutes, getMetadata]);

    // Derived (not stored) auto-selected sell token, so no setState is needed when routes resolve.
    const autoSellToken = useMemo(() => {
        if (sellTokenOverridden || !payWithRoutes?.length || !payWithTokens) return undefined;
        if (userWallet && !userTokens) return undefined; // balances not resolved → keep caller's token

        const held = (mint: string) => (balanceByMint.get(mint) ?? 0) > 0;
        // Prefer a held direct-route input; else keep the caller's token if viable; else any held input.
        let nextMint = payWithRoutes.find((route) => route.steps.length === 1 && held(route.inputMint))?.inputMint;
        if (!nextMint) {
            if (payWithTokens.some((token) => token.mint === form.sellToken?.mint)) return undefined;
            nextMint = payWithTokens.find((token) => held(token.mint))?.mint;
        }
        if (!nextMint || nextMint === form.sellToken?.mint) return undefined;
        return getMetadata(nextMint);
    }, [
        sellTokenOverridden,
        payWithRoutes,
        payWithTokens,
        userWallet,
        userTokens,
        balanceByMint,
        form.sellToken,
        getMetadata
    ]);

    const effectiveSellToken = autoSellToken ?? form.sellToken;
    // Keep form identity when no auto-selection so quote request args stay reference-stable.
    const effectiveForm = useMemo(
        () => (autoSellToken ? { ...form, sellToken: autoSellToken } : form),
        [form, autoSellToken]
    );

    // Route the current sell token would take; >1 step means it swaps via an intermediate token.
    const selectedPayWithRoute = useMemo(
        () => payWithRoutes?.find((route) => route.inputMint === effectiveSellToken?.mint),
        [payWithRoutes, effectiveSellToken?.mint]
    );
    const routeIntermediateSymbol =
        selectedPayWithRoute && selectedPayWithRoute.steps.length > 1
            ? getMetadata(selectedPayWithRoute.steps[0].outputMint)?.symbol
            : undefined;
    const routeAsyncPossible = selectedPayWithRoute?.asyncPossible === true;

    // TODO(susd.tel): glow-withdraw redeem is intentionally not configured (redeem only via atomic
    // DEX routes / Titan fallback). If the async redeem edge is ever added, this guard blocks it
    // until the withdraw-queue initiate/claim flow + a persistent pending-withdrawal surface ship.
    const flipDisabled =
        !!payWithRoutes?.length && !!buyTokenExitRoutes?.length && buyTokenExitRoutes.every((route) => route.async);

    // Binding step minimum on the selected route (e.g. Glow's 10 USD.tel deposit min), valued in
    // USD so the caller can gate the sell-side amount without cross-token unit comparisons.
    const minStepMints = useMemo(
        () => selectedPayWithRoute?.steps.filter((step) => step.minInputAmount).map((step) => step.inputMint),
        [selectedPayWithRoute]
    );
    const { getUsdPrice } = useOraclePrices(minStepMints);
    const routeMinInput = useMemo(
        () =>
            maxSwapRouteMinInputUsd(
                selectedPayWithRoute ? [selectedPayWithRoute] : undefined,
                getMetadata,
                getUsdPrice
            ),
        [selectedPayWithRoute, getMetadata, getUsdPrice]
    );

    return useMemo(
        () => ({
            payWithTokens,
            effectiveSellToken,
            effectiveForm,
            routeIntermediateSymbol,
            routeAsyncPossible,
            flipDisabled,
            routeMinInput
        }),
        [
            payWithTokens,
            effectiveSellToken,
            effectiveForm,
            routeIntermediateSymbol,
            routeAsyncPossible,
            flipDisabled,
            routeMinInput
        ]
    );
}
